import { Link } from 'react-router-dom';
import { CTAPair, Notice } from '@kollez/design-system';
import { PageMeta } from '@/components/PageMeta';

/**
 * /changelog — public product changelog. Monthly cadence targeted.
 * Ships with the last 3 months as seed content; add a new entry at the
 * top of ENTRIES each release.
 *
 * Voice per docs/design/system/copy-voice.md: confident + terse. Every
 * entry ends with the concrete outcome for the reader ("you can now …",
 * "your students see …"). Not marketing-cheer.
 */

type EntryKind = 'new' | 'improved' | 'fixed';

interface ChangelogEntry {
  /** ISO date (YYYY-MM-DD). Displayed in en-IN long form. */
  date: string;
  /** Category chip. */
  kind: EntryKind;
  /** Short entry title. Sentence case. */
  title: string;
  /** One or two sentences, ends with concrete outcome. */
  body: string;
  /** Optional deep-link (feature page, docs, sample). */
  href?: string;
}

const ENTRIES: ChangelogEntry[] = [
  {
    date: '2026-09-19',
    kind: 'new',
    title: 'Live opportunities on /for-students',
    body: 'Every student page now shows real jobs, internships, and training posted this week — pulled live from the public opportunity feed. Empty-state and error-state both handle gracefully; hitting the API isn\'t required to see the strip.',
    href: '/for-students',
  },
  {
    date: '2026-09-19',
    kind: 'new',
    title: 'Three-persona pages: /for-students, /for-partners, /for-institutions',
    body: 'The old three-role-cards landing has been split into three deep pages, one per audience. Each carries its own hero, feature rows, testimonial, and FAQ. The homepage narrowed to a doors-and-narrative shape.',
  },
  {
    date: '2026-09-18',
    kind: 'improved',
    title: 'Portable Badges narrative on student pages',
    body: 'A skill you earn on one drive now shows up as a portable badge across every future opportunity — displayed clearly on your profile and every recruiter\'s candidate view.',
  },
  {
    date: '2026-09-18',
    kind: 'improved',
    title: 'Workflow templates + skill picker in Opening form',
    body: 'Recruiters posting an opening can now attach a workflow template (e.g. AI/ML aptitude → coding → HR) and pick the exact skills a candidate needs — students see a match score, recruiters see verified badge holders first.',
  },
  {
    date: '2026-09-18',
    kind: 'fixed',
    title: 'Sign-off checks catch wrong-CTC offers',
    body: 'Offers released outside the approved CTC band, or with a name mismatch against the roster, now block on release. Every offer also carries a 10-minute undo window after student acceptance.',
  },
  {
    date: '2026-09-17',
    kind: 'new',
    title: 'Kollez Discover — public opportunity catalog',
    body: 'Opportunities marked "publicly visible" now appear at /public/opportunities, discoverable by any student on Kollez — even without an institution invite. Every listing is verified before it goes live.',
  },
  {
    date: '2026-09-17',
    kind: 'improved',
    title: 'DPDP data-portability + soft-delete',
    body: 'Any user can now export their full profile as JSON or CSV, or request account deletion (60-day soft-delete window before permanent removal) from Settings. Fully DPDP Act 2023 compliant.',
    href: '/privacy',
  },
  {
    date: '2026-09-15',
    kind: 'improved',
    title: 'Group roll-up: "Behind" flag with one-click call list',
    body: 'The 8am roll-up now highlights any college behind its placement target with a red "Behind" chip. Click it to get a call list of the principals responsible — for placement heads managing 5+ colleges.',
    href: '/for-institutions',
  },
  {
    date: '2026-09-12',
    kind: 'new',
    title: 'AI Career Advisor + Resume Builder (Nexie)',
    body: 'Nexie can now build a personal 6-week learning path for any student — with citations to real courses, real jobs, and their own academic record. Ships alongside an ATS-checked resume builder.',
    href: '/for-students',
  },
  {
    date: '2026-09-10',
    kind: 'improved',
    title: 'NIRF/NAAC/AICTE reports in one click',
    body: 'Every institution can now export placement data in the exact NIRF, NAAC, and AICTE-mandated formats — signed CSV + PDF, with a chain-of-custody audit log so the numbers survive scrutiny.',
    href: '/for-institutions',
  },
];

const KIND_LABEL: Record<EntryKind, string> = {
  new: 'New',
  improved: 'Improved',
  fixed: 'Fixed',
};

export function ChangelogPage() {
  const grouped = groupByMonth(ENTRIES);

  return (
    <>
      <PageMeta
        title="Changelog"
        description="What's new on Kollez. Monthly release notes of features, improvements, and fixes across the platform."
        canonical="/changelog"
      />

      {/* HERO */}
      <section className="mx-auto max-w-page-narrow px-5 py-16 lg:px-10 lg:py-24 grid gap-6">
        <div>
          <p className="eyebrow">Changelog</p>
          <h1 className="display text-ink-primary mt-2">What's new on Kollez.</h1>
          <p className="mt-6 text-lg leading-8 text-ink-secondary">
            Monthly release notes. Every entry ends with the concrete
            outcome for you — a student, a recruiter, or a TPO.
          </p>
        </div>

        <Notice tone="info">
          Want an RSS feed or an email when we ship? It's on the roadmap —
          for now, <Link className="link" to="/contact?topic=changelog">let us know</Link>{' '}
          and we'll add you to the list.
        </Notice>

        {/* ENTRIES */}
        <div className="grid gap-10 mt-4">
          {grouped.map(([month, entries]) => (
            <MonthGroup key={month} month={month} entries={entries} />
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-8 py-8 border-t border-line-tertiary">
          <h2 className="h-serif text-2xl">Want to shape what ships next?</h2>
          <p className="mt-3 text-ink-secondary">
            Every feature on this page started as a real ask from a
            TPO, recruiter, or student.
          </p>
          <div className="mt-6 flex justify-center">
            <CTAPair
              primary={{ label: 'Book a 30-min demo', to: '/contact?topic=demo' }}
              secondary={{ label: 'Share a request', to: '/contact?topic=feedback' }}
              align="center"
            />
          </div>
        </div>
      </section>
    </>
  );
}

function MonthGroup({ month, entries }: { month: string; entries: ChangelogEntry[] }) {
  return (
    <section aria-label={month}>
      <h2 className="h-serif text-xl sticky top-16 bg-secondary py-2 z-10" style={{
        color: 'rgb(var(--ink-tertiary))',
        borderBottom: '1px solid rgb(var(--line-tertiary))',
      }}>
        {month}
      </h2>
      <ol className="grid gap-6 mt-4">
        {entries.map((e) => (
          <EntryRow key={`${e.date}-${e.title}`} entry={e} />
        ))}
      </ol>
    </section>
  );
}

function EntryRow({ entry }: { entry: ChangelogEntry }) {
  const dateLabel = new Date(entry.date).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return (
    <li className="grid gap-2">
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className="text-xs px-2 py-0.5 rounded-full font-medium"
          style={{
            background:
              entry.kind === 'new'      ? 'rgb(var(--bg-info))' :
              entry.kind === 'improved' ? 'rgb(var(--bg-success))' :
                                           'rgb(var(--bg-warning))',
            color:
              entry.kind === 'new'      ? 'rgb(var(--ink-accent))' :
              entry.kind === 'improved' ? 'rgb(var(--ink-success))' :
                                           'rgb(var(--ink-warning))',
          }}
        >
          {KIND_LABEL[entry.kind]}
        </span>
        <span className="text-xs text-ink-tertiary tabular-nums">{dateLabel}</span>
      </div>
      <h3 className="font-serif text-lg leading-snug">{entry.title}</h3>
      <p className="text-ink-secondary">{entry.body}</p>
      {entry.href && (
        <Link className="link text-sm mt-1" to={entry.href}>
          See it in action →
        </Link>
      )}
    </li>
  );
}

/** Group entries by "Month YYYY", preserving reverse-chronological order. */
function groupByMonth(entries: ChangelogEntry[]): Array<[string, ChangelogEntry[]]> {
  const seen = new Map<string, ChangelogEntry[]>();
  for (const e of entries) {
    const month = new Date(e.date).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    const list = seen.get(month) ?? [];
    list.push(e);
    seen.set(month, list);
  }
  return Array.from(seen.entries());
}
